import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spin, Result, Button, Typography } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  LoadingOutlined,
  HomeOutlined,
  CreditCardOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { fetchSubInfo, logout } from '../../../store/slices/auth.slice';
import subService from '../../../services/sub/sub.service';
import { all_routes } from '../../../router/all_routes';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface PaymentVerificationResult {
  message: string;
  subscription: {
    status: string;
    type: string;
    referenceCode: string;
    expiryDate: string;
  };
}

const SubscriptionCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PaymentVerificationResult | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      try {

        
        // localStorage'dan checkout session token'ını al
        const token = localStorage.getItem('checkoutsession');
        const conversationId = localStorage.getItem('conversationId');
        


        
        if (!token) {
          setResult({
            message: 'Ödeme doğrulama token\'ı bulunamadı. Doğrulama zaten yapılmış olabilir.',
            subscription: {
              status: 'error',
              type: 'monthly',
              referenceCode: '',
              expiryDate: ''
            }
          });
          setLoading(false);
          return;
        }

        // 🔒 KRITIK GÜVENLİK: Token'ı hemen sil ki ikinci kez çalışırsa token bulamasın
        localStorage.removeItem('checkoutsession');
        localStorage.removeItem('conversationId');
                
        // Backend'e doğrulama isteği gönder - IYZICO conversation ID ile
        const verificationResult = await subService.verifyPayment(token, conversationId!);


        // Response'u state'e kaydet
        setResult({
          message: verificationResult.message || 'Ödeme işlemi tamamlandı',
          subscription: verificationResult.subscription
        });

        // Eğer abonelik aktif ise
        if (verificationResult.subscription.status === 'active') {
          
          // Mevcut kullanıcı için sub bilgilerini yenile
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          if (user.companyId) {
            dispatch(fetchSubInfo(user.companyId));
          }
          
          
          // 3 saniye countdown başlat
          setIsRedirecting(true);
          setCountdown(3);
          
          const countdownInterval = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                // Logout yap ve login sayfasına yönlendir
                handleLogoutAndRedirect();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {

        }

      } catch (error: any) {
        setResult({
          message: error.response?.data?.message || 'Ödeme doğrulama sırasında bir hata oluştu.',
          subscription: {
            status: 'error',
            type: 'monthly',
            referenceCode: '',
            expiryDate: ''
          }
        });
        
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, []); // 🔒 GÜVENLIK: Dependency array'i boş - sadece mount'ta çalışsın

  const handleGoToDashboard = () => {

    navigate(all_routes.adminDashboard);
  };

  const handleGoToSettings = () => {

    navigate(all_routes.companySettings + '?tab=payment-settings');
  };

  const handleRetryPayment = () => {
    navigate(all_routes.companySettings + '?tab=payment-settings');
  };

  const handleLogoutAndRedirect = () => {
    dispatch(logout());
    navigate(all_routes.login);
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Card style={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
              <div style={{ padding: '40px 20px' }}>
                <Spin 
                  indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
                  size="large"
                />
                <div style={{ marginTop: 24 }}>
                  <Title level={4}>Ödeme Doğrulanıyor...</Title>
                  <Text type="secondary">
                    Abonelik ödemeniz doğrulanıyor, lütfen bekleyin...
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Card style={{ maxWidth: 600, width: '100%' }}>
            {result?.subscription.status === 'active' ? (
              <Result
                icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 72 }} />}
                title={
                  <Title level={3} style={{ color: '#52c41a', marginBottom: 16 }}>
                    🎉 Aboneliğiniz Güncellendi!
                  </Title>
                }
                subTitle={
                  <div>
                    <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {result.message}
                    </Text>
                    <Text style={{ fontSize: 16, display: 'block', marginBottom: 12, color: '#52c41a', fontWeight: 600 }}>
                      Abonelik değişikliğiniz başarıyla işlendi!
                    </Text>
                    <Text style={{ fontSize: 14, display: 'block', marginBottom: 8, color: '#1890ff' }}>
                      Yeni abonelik ayarlarınız aktif edildi.
                    </Text>
                    {isRedirecting && (
                      <div style={{ marginTop: 20, padding: 16, background: '#f0f9ff', borderRadius: 8, border: '1px solid #1890ff' }}>
                        <Text style={{ fontSize: 16, display: 'block', marginBottom: 8, color: '#1890ff', fontWeight: 600 }}>
                          🔄 Güvenlik nedeniyle giriş sayfasına yönlendiriliyorsunuz...
                        </Text>
                        <Text style={{ fontSize: 14, color: '#666' }}>
                          {countdown} saniye sonra otomatik olarak giriş sayfasına yönlendirileceksiniz.
                        </Text>
                      </div>
                    )}
                  </div>
                }
                extra={!isRedirecting ? [
                  <Button 
                    type="primary" 
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                    size="large"
                  >
                    Ana Sayfaya Git
                  </Button>,
                  <Button 
                    key="settings"
                    icon={<SettingOutlined />}
                    onClick={handleGoToSettings}
                  >
                    Abonelik Ayarları
                  </Button>
                ] : [
                  <Button 
                    key="manual-redirect"
                    type="primary"
                    icon={<HomeOutlined />}
                    onClick={handleLogoutAndRedirect}
                    size="large"
                  >
                    Hemen Giriş Sayfasına Git
                  </Button>
                ]}
              />
            ) : (
              <Result
                icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 72 }} />}
                title={
                  <Title level={3} style={{ color: '#ff4d4f', marginBottom: 16 }}>
                    {result?.subscription.status && result.subscription.status !== 'active' 
                      ? 'Abonelik Güncellenemedi' 
                      : 'Ödeme Başarısız'
                    }
                  </Title>
                }
                subTitle={
                  <div>
                    <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {result?.message || 'Ödeme işlemi tamamlanamadı.'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                      {result?.subscription.status && result.subscription.status !== 'active'
                        ? `Abonelik durumu: ${result.subscription.status}`
                        : 'Lütfen kart bilgilerinizi kontrol edip tekrar deneyin.'
                      }
                    </Text>
                  </div>
                }
                extra={[
                  <Button 
                    type="primary" 
                    key="retry"
                    icon={<CreditCardOutlined />}
                    onClick={handleRetryPayment}
                    size="large"
                  >
                    Tekrar Dene
                  </Button>,
                  <Button 
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                  >
                    Ana Sayfaya Git
                  </Button>
                ]}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCallback; 